import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type PrivacyErasureBlockerCode, type PrivacyExportRequestView } from "../../api/client";
import { ConfirmDialog } from "../../components/feedback/ConfirmDialog";
import { EmptyState, ErrorState, LoadingState } from "../../components/feedback/AsyncState";

function timestamp(value: string | null | undefined): string {
  return value ? new Date(value).toLocaleString() : "Not recorded";
}

function blockerMessage(blocker: PrivacyErasureBlockerCode): string {
  return blocker === "SoleWorkspaceOwner"
    ? "Transfer or add another active Owner in every workspace where you are the sole Owner."
    : "An active Platform Administrator account cannot use self-service erasure.";
}

function exportStatusDetail(exportRequest: PrivacyExportRequestView): string {
  if (exportRequest.status === "Available") {
    return exportRequest.expiresAt
      ? `Available until ${timestamp(exportRequest.expiresAt)}.`
      : "Available for a short time.";
  }
  if (exportRequest.status === "Failed") return "The export build did not complete. You can request a new export.";
  if (exportRequest.status === "Expired") return "This export file has expired and is no longer downloadable.";
  if (exportRequest.status === "Canceled") return "This export request was canceled.";
  return "The private export is being prepared by a durable background job.";
}

function triggerDownload(blob: Blob, fileName: string | null): void {
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = fileName ?? "contentforge-personal-data.zip";
  anchor.style.display = "none";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 0);
}

/**
 * Personal-data self-service surface. Credentials and the resulting opaque
 * reauthentication proof intentionally never enter React state, query cache,
 * browser storage, URLs, or audit text. They are read from an uncontrolled
 * password input only when the confirmed erasure mutation begins and cleared
 * before either network request is awaited.
 */
export function PrivacyPage(): React.JSX.Element {
  const client = useQueryClient();
  const [exportConfirmationOpen, setExportConfirmationOpen] = useState(false);
  const [erasureConfirmationOpen, setErasureConfirmationOpen] = useState(false);
  const passwordInput = useRef<HTMLInputElement>(null);
  const privacy = useQuery({
    queryKey: ["privacy", "me"],
    queryFn: api.getMyPrivacy,
    retry: false,
    refetchInterval: 10_000
  });
  const exports = useQuery({
    queryKey: ["privacy", "exports"],
    queryFn: () => api.listMyPrivacyExports(),
    retry: false,
    refetchInterval: 10_000
  });
  const refresh = async (): Promise<void> => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["privacy", "me"] }),
      client.invalidateQueries({ queryKey: ["privacy", "exports"] })
    ]);
  };
  const requestExport = useMutation({
    mutationFn: async () => {
      const etag = privacy.data?.etag;
      if (!etag) throw new Error("privacy_etag_unavailable");
      return api.requestMyPrivacyExport(etag);
    },
    onSuccess: async () => {
      setExportConfirmationOpen(false);
      await refresh();
    }
  });
  const requestErasure = useMutation({
    mutationFn: async () => {
      let credential = passwordInput.current?.value ?? "";
      if (passwordInput.current) passwordInput.current.value = "";
      if (!credential) throw new Error("privacy_reauthentication_required");
      const etag = privacy.data?.etag;
      if (!etag) throw new Error("privacy_etag_unavailable");

      // The opaque proof lives only in this mutation call and is consumed by
      // the immediately following request. It is never copied into state.
      const proof = await api.issueMyPrivacyReauthenticationProof(credential, etag);
      credential = "";
      return api.requestMyPrivacyErasure(proof.data.proof, proof.etag ?? etag);
    },
    onSuccess: async () => {
      setErasureConfirmationOpen(false);
      await refresh();
    },
    onError: () => {
      if (passwordInput.current) passwordInput.current.value = "";
    }
  });
  const downloadExport = useMutation({
    mutationFn: api.downloadMyPrivacyExport,
    onSuccess: result => triggerDownload(result.blob, result.fileName)
  });

  useEffect(() => {
    if (!erasureConfirmationOpen && passwordInput.current) passwordInput.current.value = "";
  }, [erasureConfirmationOpen]);

  if (privacy.isLoading) return <LoadingState label="Loading personal-data controls…" />;
  if (privacy.isError) return <ErrorState error={privacy.error} retry={() => void refresh()} />;

  const details = privacy.data?.data;
  if (!details) return <EmptyState title="Personal-data controls are unavailable" detail="Refresh this page to restore your current account privacy status." />;
  const listedExports = exports.data?.data ?? [];
  const exportRequests = details.latestExport && !listedExports.some(item => item.id === details.latestExport?.id)
    ? [details.latestExport, ...listedExports]
    : listedExports;
  const eligibility = details.erasureEligibility;

  return <section className="stack">
    <div className="page-heading"><div><p className="eyebrow">PERSONAL DATA</p><h1>Privacy controls</h1><p>Request a short-lived export of your account, memberships, invitations and your own history, or begin a protected account-erasure request.</p></div><button type="button" className="button-secondary" onClick={() => void refresh()}>Refresh</button></div>

    <section className="panel stack"><div className="section-heading"><div><p className="eyebrow">EXPORT</p><h2>Download your personal data</h2><p>The export includes only your account, memberships, invitations, and your own security and audit history. It is prepared asynchronously and expires after a short time.</p></div></div>
      <div className="actions"><button type="button" className="button-primary" disabled={requestExport.isPending} onClick={() => setExportConfirmationOpen(true)}>{requestExport.isPending ? "Requesting export…" : "Request personal-data export"}</button></div>
      {exports.isError && <p className="field-error" role="alert">The export history could not be loaded. Your latest export status remains below when available.</p>}
      {exportRequests.length ? <ol className="timeline">{exportRequests.map(item => <li key={item.id}><strong>{item.status}</strong><span>Requested {timestamp(item.requestedAt)} · {exportStatusDetail(item)}</span>{item.resultCode && <small>Result: {item.resultCode}</small>}{item.status === "Available" && <button type="button" className="button-secondary" disabled={downloadExport.isPending} onClick={() => void downloadExport.mutateAsync(item.id)}>{downloadExport.isPending ? "Preparing download…" : "Download export"}</button>}</li>)}</ol> : <p className="muted">No personal-data export has been requested yet.</p>}
      {requestExport.error && <p className="field-error" role="alert">The export request could not be completed. Refresh the current status before trying again.</p>}
      {downloadExport.error && <p className="field-error" role="alert">The export file is no longer available. Request a fresh export if needed.</p>}
    </section>

    <section className="panel stack"><div className="section-heading"><div><p className="eyebrow">ERASURE</p><h2>Request account erasure</h2><p>Erasure removes mutable membership, invitation, login and claim data. Required immutable audit, approval and cost evidence remains as pseudonymous historical evidence.</p></div>{eligibility.canRequest ? <span className="status status-passed">Eligible</span> : <span className="status status-blocked">Blocked</span>}</div>
      {eligibility.canRequest ? <><p>Confirm with your current password. The password is read only to create a one-time proof for this request and is cleared before network processing begins.</p><button type="button" className="button-danger" disabled={requestErasure.isPending} onClick={() => setErasureConfirmationOpen(true)}>{requestErasure.isPending ? "Requesting erasure…" : "Request account erasure"}</button></> : <><p className="field-error">Self-service erasure cannot be requested until these responsibilities are resolved:</p><ul>{eligibility.blockers.map(blocker => <li key={blocker}>{blockerMessage(blocker)}</li>)}</ul></>}
      {details.latestErasure && <p className="muted">Latest erasure request: {details.latestErasure.status} · requested {timestamp(details.latestErasure.requestedAt)}{details.latestErasure.resultCode ? ` · ${details.latestErasure.resultCode}` : ""}</p>}
      {requestErasure.error && <p className="field-error" role="alert">The erasure request could not be completed. Check eligibility and reauthenticate again before retrying.</p>}
    </section>

    <ConfirmDialog open={exportConfirmationOpen} title="Request personal-data export?" message="A short-lived archive containing only your own supported records will be prepared. This does not change workspace content or remote publishing." confirmLabel="Request export" onCancel={() => setExportConfirmationOpen(false)} onConfirm={() => void requestExport.mutateAsync()} />
    {eligibility.canRequest && <ErasureConfirmationDialog open={erasureConfirmationOpen} pending={requestErasure.isPending} passwordInput={passwordInput} onCancel={() => setErasureConfirmationOpen(false)} onConfirm={() => void requestErasure.mutateAsync()} />}
  </section>;
}

function ErasureConfirmationDialog({ open, pending, passwordInput, onCancel, onConfirm }: {
  open: boolean;
  pending: boolean;
  passwordInput: React.RefObject<HTMLInputElement | null>;
  onCancel(): void;
  onConfirm(): void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (!dialog.current) return;
    if (open && !dialog.current.open) dialog.current.showModal();
    if (!open && dialog.current.open) dialog.current.close();
  }, [open]);

  return <dialog ref={dialog} aria-labelledby="privacy-erasure-confirm-title" onCancel={event => { event.preventDefault(); onCancel(); }}>
    <form className="dialog-content" onSubmit={event => { event.preventDefault(); onConfirm(); }}>
      <h2 id="privacy-erasure-confirm-title">Confirm account erasure request</h2>
      <p>This begins a durable deletion/anonymization request. It does not erase required pseudonymous audit, approval, or cost evidence. Verify the current password for this account to continue.</p>
      <label>Current password<input ref={passwordInput} type="password" autoComplete="current-password" required disabled={pending} /></label>
      <div className="actions"><button type="button" className="button-secondary" disabled={pending} onClick={onCancel}>Cancel</button><button type="submit" className="button-danger" disabled={pending}>{pending ? "Verifying…" : "Confirm and request erasure"}</button></div>
    </form>
  </dialog>;
}
